import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gymApi } from '@/lib/api';
import { Gym, MembershipPlan, GymClass } from '@/types';
import { reportError, reportErrorWithContext, setPageArea } from '@/lib/sentry';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { sanitizePhone, COUNTRY_CODES } from '@/lib/phone-utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Building2,
    User,
    Mail,
    Phone,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Loader2,
    Star,
    Check,
    Download,
    Share2,
    QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { ClientPhotoCapture } from '@/components/ClientPhotoCapture';

export default function JoinGymPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [gym, setGym] = useState<Gym | null>(null);
    const [plans, setPlans] = useState<MembershipPlan[]>([]);
    const [classes, setClasses] = useState<GymClass[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [photoFile, setPhotoFile] = useState<File | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        age: '',
        plan_id: '',
        selected_classes: [] as string[]
    });

    const [countryCode, setCountryCode] = useState('+503');

    const [registrationResult, setRegistrationResult] = useState<{
        client_name: string;
        qr_code: string;
        membership_type: string;
        expiry_date: string;
    } | null>(null);

    useEffect(() => {
        setPageArea("onboarding");
    }, []);

    useEffect(() => {
        async function fetchGymData() {
            if (!slug) return;
            try {
                const res = await gymApi.getGymBySlug(slug);
                if (res.success && res.gym) {
                    setGym(res.gym);
                    const [plansRes, classesRes] = await Promise.all([
                        gymApi.listMembershipPlans(res.gym.gym_id, false),
                        gymApi.listClasses(null, res.gym.gym_id)
                    ]);
                    if (plansRes.success) setPlans(plansRes.plans);
                    if (classesRes.success) setClasses(classesRes.classes.filter(c => c.status === 'active'));
                }
            } catch (error) {
                console.error('Error fetching gym data:', error);
                reportErrorWithContext(error, {
                    operation: "join_gym_load_data",
                    area: "onboarding",
                    contextName: "gym_lookup",
                    contextData: {
                        slug,
                        url: window.location.href,
                        userAgent: navigator.userAgent,
                    },
                    extra: { slug_attempted: slug, error_type: "gym_not_found" },
                });
            } finally {
                setIsLoading(false);
            }
        }
        fetchGymData();
    }, [slug]);

    const handleNext = () => {
        if (step === 1 && (!formData.name || !formData.email || !formData.phone || !formData.age)) {
            toast.error("Por favor completa todos tus datos personales incluyendo tu edad");
            return;
        }
        if (step === 2 && !formData.plan_id) {
            toast.error("Por favor selecciona un plan de membresía");
            return;
        }
        setStep(step + 1);
    };

    const handleBack = () => setStep(step - 1);

    const handleSubmit = async () => {
        if (!gym) return;
        setIsSubmitting(true);
        try {
            const res = await gymApi.registerGuestWithPhoto({
                gym_id: gym.gym_id,
                plan_id: formData.plan_id,
                name: formData.name,
                email: formData.email,
                phone: sanitizePhone(formData.phone, countryCode),
                age: Number(formData.age)
            }, photoFile);

            if (res.success) {
                setRegistrationResult({
                    client_name: res.client_name,
                    qr_code: res.qr_code,
                    membership_type: res.membership_type,
                    expiry_date: new Date(res.expiry_date).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    })
                });

                toast.success("¡Inscripción exitosa!");
                setStep(4);
            } else {
                toast.error("Error: " + (res.error || "Ocurrió un problema"));
            }
        } catch (error: any) {
            console.error("Client Registration Error:", error);
            reportError(error, {
                operation: 'client_registration',
                area: 'onboarding',
                extra: { gym_id: gym.gym_id, plan_id: formData.plan_id }
            });
            toast.error("Error al procesar la inscripción: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen relative flex flex-col items-center justify-center overflow-hidden">
                <div className="fixed inset-0 z-0">
                    <img
                        src="/hero-vibrant.png"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-lg" />
                </div>

                <div className="relative z-10 flex flex-col items-center">
                    <div className="h-20 w-20 bg-orange-600/20 rounded-3xl flex items-center justify-center mb-8 border border-orange-500/30 animate-pulse">
                        <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
                    </div>
                    <p className="text-white font-black uppercase tracking-[0.3em] text-sm animate-pulse">Cargando Experiencia...</p>
                </div>
            </div>
        );
    }

    if (!gym) {
        return (
            <div className="min-h-screen relative flex flex-col items-center justify-center p-6 overflow-hidden">
                <div className="fixed inset-0 z-0">
                    <img
                        src="/hero-vibrant.png"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-lg" />
                </div>

                <div className="relative z-10 w-full max-w-md text-center">
                    <div className="h-24 w-24 bg-red-500/10 rounded-[2.5rem] flex items-center justify-center mb-8 border border-red-500/20 mx-auto backdrop-blur-xl">
                        <Building2 className="h-12 w-12 text-red-500" />
                    </div>
                    <h1 className="text-4xl font-black mb-4 text-white tracking-tighter">Gimnasio no encontrado</h1>
                    <p className="text-slate-400 font-medium mb-10 leading-relaxed text-lg">
                        El enlace parece haber expirado o la dirección es incorrecta. Verifica con tu club el link de acceso.
                    </p>
                    <Button
                        onClick={() => navigate('/')}
                        className="h-16 px-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-black text-lg border border-white/10 backdrop-blur-md transition-all w-full"
                    >
                        Volver al inicio
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative overflow-hidden selection:bg-orange-500/30">
            {/* Background Image with Blur Overlay */}
            <div className="fixed inset-0 z-0">
                <img
                    src="/hero-vibrant.png"
                    alt="Background"
                    className="w-full h-full object-cover scale-105"
                />
                <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[5px]" />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-slate-950/80" />
            </div>

            <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 md:py-20">
                {/* Header */}
                <div className="text-center mb-12 animate-in fade-in slide-in-from-top duration-700">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 text-orange-400 text-[10px] font-black uppercase tracking-widest mb-6 border border-orange-500/20 backdrop-blur-md">
                        <Star className="h-3 w-3 fill-orange-500" /> Inscripción Digital
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-4 text-white">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                            {gym.name}
                        </span>
                    </h1>
                    <p className="text-slate-300 max-w-lg mx-auto text-lg font-medium leading-relaxed">
                        Estás a pocos pasos de transformar tu vida. Completa tu registro y únete a nuestra mejor versión.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    {/* Progress Sidebar - Desktop */}
                    <div className="hidden lg:flex flex-col gap-8 col-span-3 pt-4">
                        {[
                            { step: 1, label: 'Datos Básicos', desc: 'Identificación' },
                            { step: 2, label: 'Tu Plan', desc: 'Suscripción' },
                            { step: 3, label: 'Confirmación', desc: 'Finalizar' }
                        ].map((s) => (
                            <div key={s.step} className={cn(
                                "flex items-center gap-4 transition-all duration-500",
                                step >= s.step ? "opacity-100 translate-x-1" : "opacity-30"
                            )}>
                                <div className={cn(
                                    "h-12 w-12 rounded-2xl flex items-center justify-center font-black transition-all shrink-0 border-2",
                                    step === s.step ? "bg-orange-600 border-orange-400 text-white shadow-xl shadow-orange-600/30 scale-110" :
                                        step > s.step ? "bg-orange-600/20 border-orange-600/40 text-orange-400" : "bg-slate-800/50 border-slate-700/50 text-slate-500"
                                )}>
                                    {step > s.step ? <Check className="h-6 w-6 stroke-[3px]" /> : s.step}
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-black text-white leading-none mb-1.5">{s.label}</span>
                                    <span className="text-[10px] text-slate-400 uppercase font-black tracking-widest">{s.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Form Content */}
                    <div className="lg:col-span-9">
                        <Card className="border-none shadow-2xl rounded-[3rem] overflow-hidden bg-white/95 backdrop-blur-xl">
                            <div className="h-3 bg-gradient-to-r from-orange-600 to-amber-500" />
                            <CardContent className="p-8 md:p-16">
                                {/* Step 1: Personal Info */}
                                {step === 1 && (
                                    <div className="space-y-8 animate-in">
                                        <div className="grid grid-cols-1 gap-8">
                                            <div className="space-y-3">
                                                <Label htmlFor="name" className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">¿Cómo te llamas?</Label>
                                                <div className="relative group">
                                                    <User className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-orange-500 transition-colors" />
                                                    <Input
                                                        id="name"
                                                        placeholder="Nombre y Apellidos"
                                                        value={formData.name}
                                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                        className="h-16 pl-14 bg-slate-50 border-slate-100 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500/30 transition-all text-lg text-slate-900 font-semibold"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                <div className="space-y-3">
                                                    <Label htmlFor="email" className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">Tu mejor email</Label>
                                                    <div className="relative group">
                                                        <Mail className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-orange-500 transition-colors" />
                                                        <Input
                                                            id="email"
                                                            type="email"
                                                            placeholder="ejemplo@correo.com"
                                                            value={formData.email}
                                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                            className="h-16 pl-14 bg-slate-50 border-slate-100 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500/30 transition-all text-lg text-slate-900 font-semibold"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-3">
                                                    <Label htmlFor="age" className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">Tu Edad</Label>
                                                    <div className="relative group">
                                                        <User className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-orange-500 transition-colors" />
                                                        <Input
                                                            id="age"
                                                            type="number"
                                                            placeholder="Ej. 25"
                                                            value={formData.age}
                                                            onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                                            className="h-16 pl-14 bg-slate-50 border-slate-100 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500/30 transition-all text-lg text-slate-900 font-semibold"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-3">
                                                    <Label htmlFor="phone" className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">Teléfono de contacto</Label>
                                                    <div className="flex gap-2">
                                                        <Select value={countryCode} onValueChange={setCountryCode}>
                                                            <SelectTrigger className="h-16 w-[95px] bg-slate-50 border-slate-100 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500/30 transition-all font-bold text-slate-900 text-sm px-3">
                                                                <SelectValue placeholder="País" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {COUNTRY_CODES.map((c) => (
                                                                    <SelectItem key={c.code} value={c.code}>
                                                                        <span className="flex items-center gap-2">
                                                                            <span className="text-lg">{c.flag}</span>
                                                                            <span className="font-semibold text-xs">{c.code}</span>
                                                                        </span>
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <div className="relative group flex-1">
                                                            <Phone className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-orange-500 transition-colors" />
                                                            <Input
                                                                id="phone"
                                                                placeholder="8978 8978"
                                                                value={formData.phone}
                                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                                className="h-16 pl-14 bg-slate-50 border-slate-100 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500/30 transition-all text-lg text-slate-900 font-semibold"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <ClientPhotoCapture
                                            file={photoFile}
                                            onFileChange={setPhotoFile}
                                            disabled={isSubmitting}
                                            label="Tu foto (opcional)"
                                            hint="Se comprima en tu navegador antes de subirla. Puedes omitirla y agregarla después."
                                            variant="light"
                                        />
                                    </div>
                                )}

                                {/* Step 2: Plans */}
                                {step === 2 && (
                                    <div className="space-y-8 animate-in">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {plans.map((plan) => (
                                                <div
                                                    key={plan.id}
                                                    onClick={() => setFormData({ ...formData, plan_id: plan.id })}
                                                    className={cn(
                                                        "p-8 rounded-[2.5rem] border-2 cursor-pointer transition-all duration-300 flex flex-col justify-between min-h-[220px] relative group",
                                                        formData.plan_id === plan.id
                                                            ? 'border-orange-600 bg-orange-50/50 shadow-xl shadow-orange-600/10 ring-4 ring-orange-500/5'
                                                            : 'border-slate-100 hover:border-orange-500/30 bg-slate-50'
                                                    )}
                                                >
                                                    {plan.name.toLowerCase().includes('premium') && (
                                                        <Badge className="absolute top-5 right-5 bg-orange-600 text-white border-none text-[8px] font-black uppercase tracking-tighter">Recommended</Badge>
                                                    )}

                                                    <div>
                                                        <h4 className="font-black text-2xl mb-1 text-slate-900 group-hover:text-orange-600 transition-colors">{plan.name}</h4>
                                                        <p className="text-sm text-slate-500 font-bold leading-relaxed">
                                                            {plan.description || `${plan.duration_days} días de acceso ilimitado`}
                                                        </p>
                                                    </div>

                                                    <div className="mt-8 flex items-baseline gap-1.5">
                                                        <span className="text-4xl font-black text-slate-900 group-hover:scale-110 transition-transform origin-left">${Number(plan.price).toFixed(2)}</span>
                                                        <span className="text-slate-400 font-black text-[10px] uppercase tracking-widest">/ total</span>
                                                    </div>

                                                    <div className={cn(
                                                        "absolute bottom-6 right-6 h-12 w-12 rounded-full flex items-center justify-center transition-all duration-500",
                                                        formData.plan_id === plan.id ? "bg-orange-600 text-white shadow-lg scale-100 rotate-0" : "bg-slate-200 text-transparent scale-50 rotate-90 opacity-0"
                                                    )}>
                                                        <Check className="h-7 w-7 stroke-[3px]" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Step 3: Confirmation */}
                                {step === 3 && (
                                    <div className="space-y-8 animate-in fade-in duration-500 text-center py-4">
                                        <div className="h-24 w-24 bg-primary/10 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                                            <CheckCircle2 className="h-12 w-12 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="text-3xl font-black mb-2 text-slate-900">¡Casi estamos listos!</h3>
                                            <p className="text-slate-600 font-medium">Revisa que tus datos sean correctos antes de finalizar.</p>
                                        </div>

                                        <div className="max-w-md mx-auto p-8 rounded-[2.5rem] bg-white border-2 border-slate-100 shadow-xl shadow-slate-200/50 text-left space-y-6 relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-full h-1 gradient-primary opacity-50" />

                                            <div className="flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                                <span>Detalle de Inscripción</span>
                                                <span>Valor</span>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Plan Seleccionado</span>
                                                        <span className="text-slate-900 font-black">
                                                            {plans.find(p => p.id === formData.plan_id)?.name}
                                                        </span>
                                                    </div>
                                                    <span className="font-black text-xl text-slate-900">
                                                        ${Number(plans.find(p => p.id === formData.plan_id)?.price || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="pt-2 flex justify-between items-center border-t-2 border-dashed border-slate-100">
                                                <div className="flex flex-col">
                                                    <span className="font-black text-primary uppercase text-[10px] tracking-[0.1em]">Total a pagar</span>
                                                    <span className="text-xs text-slate-400 font-bold italic">Impuestos incluidos</span>
                                                </div>
                                                <span className="text-4xl font-black text-primary drop-shadow-sm">
                                                    ${Number(plans.find(p => p.id === formData.plan_id)?.price || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Success */}
                                {step === 4 && registrationResult && (
                                    <div className="text-center py-4 animate-in fade-in zoom-in duration-500">
                                        <div className="mb-8 relative inline-block">
                                            <div className="absolute inset-0 bg-success/20 blur-3xl rounded-full scale-150 animate-pulse-glow" />
                                            <div className="relative h-20 w-20 bg-success rounded-full flex items-center justify-center mx-auto shadow-glow">
                                                <CheckCircle2 className="h-10 w-10 text-white" />
                                            </div>
                                        </div>

                                        <h2 className="text-3xl font-black mb-2 text-slate-900">¡Bienvenido a {gym.name}!</h2>
                                        <p className="text-slate-600 font-medium mb-8">
                                            Tu inscripción ha sido completada con éxito.
                                        </p>

                                        {/* Member Card */}
                                        <div className="max-w-sm mx-auto mb-10 overflow-hidden rounded-[2.5rem] bg-slate-900 border border-white/10 shadow-2xl relative group">
                                            {/* Card Header Background */}
                                            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-r from-orange-600 to-amber-500 opacity-20 blur-xl" />

                                            <div className="p-8 relative z-10">
                                                <div className="flex justify-between items-start mb-6">
                                                    <div className="text-left">
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-primary/80 mb-1 block">Member Card</span>
                                                        <h3 className="text-xl font-bold text-white">{registrationResult.client_name}</h3>
                                                    </div>
                                                    <div className="h-10 w-10 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md">
                                                        <Building2 className="h-5 w-5 text-white/70" />
                                                    </div>
                                                </div>

                                                {/* QR Code Section */}
                                                <div className="bg-white p-6 rounded-3xl mb-6 shadow-xl relative group-hover:scale-105 transition-transform duration-500">
                                                    <div className="absolute inset-x-0 -bottom-4 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Badge className="bg-primary text-white text-[9px]">YOUR ACCESS CODE</Badge>
                                                    </div>
                                                    <img
                                                        src={`https://quickchart.io/qr?text=${encodeURIComponent(registrationResult.qr_code)}&size=300`}
                                                        alt="Membership QR Code"
                                                        className="w-full h-auto aspect-square rounded-xl"
                                                    />
                                                </div>

                                                {/* Membership Info */}
                                                <div className="grid grid-cols-2 gap-4 text-left border-t border-white/10 pt-6">
                                                    <div>
                                                        <p className="text-[9px] font-black uppercase text-slate-500 tracking-wider mb-1">Plan</p>
                                                        <p className="text-sm font-bold text-white truncate">{registrationResult.membership_type}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-black uppercase text-slate-500 tracking-wider mb-1">Válido Hasta</p>
                                                        <p className="text-sm font-bold text-white">{registrationResult.expiry_date}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Bottom bar */}
                                            <div className="bg-white/5 py-4 px-8 border-t border-white/10 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Status: Active</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <QrCode className="h-3 w-3 text-slate-400" />
                                                    <span className="text-[10px] font-mono text-slate-500">{registrationResult.qr_code.substring(0, 8)}...</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
                                            <Button
                                                variant="outline"
                                                className="h-14 px-8 rounded-2xl border-slate-200 dark:border-slate-800 text-lg font-bold gap-3 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all"
                                                onClick={async () => {
                                                    const url = `https://quickchart.io/qr?text=${encodeURIComponent(registrationResult.qr_code)}&size=500`;
                                                    try {
                                                        const response = await fetch(url);
                                                        const blob = await response.blob();
                                                        const blobUrl = window.URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = blobUrl;
                                                        link.download = `QR_Acceso_${registrationResult.client_name.replace(/\s+/g, '_')}.png`;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                        window.URL.revokeObjectURL(blobUrl);
                                                    } catch (e) {
                                                        window.open(url, '_blank');
                                                    }
                                                }}
                                            >
                                                <Download className="h-5 w-5" />
                                                Descargar QR
                                            </Button>
                                            <Button
                                                className="h-14 px-8 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white text-lg font-bold gap-3 shadow-glow hover:scale-105 transition-all"
                                                onClick={() => {
                                                    if (navigator.share) {
                                                        navigator.share({
                                                            title: `Mi membresía en ${gym.name}`,
                                                            text: `¡Me acabo de inscribir a ${gym.name}! Mi código de acceso es ${registrationResult.qr_code}`,
                                                            url: window.location.href
                                                        });
                                                    } else {
                                                        toast.success("Toma una captura de pantalla de tu código QR");
                                                    }
                                                }}
                                            >
                                                <Share2 className="h-5 w-5" />
                                                Compartir Membresía
                                            </Button>
                                        </div>

                                        <p className="mt-8 text-xs text-slate-500 font-medium">
                                            Por favor toma una captura de pantalla o descarga tu código QR. <br />
                                            Lo necesitarás para registrar tu entrada en el gimnasio.
                                        </p>
                                    </div>
                                )}

                                {/* Navigation Buttons */}
                                {step < 4 && (
                                    <div className="flex flex-col md:flex-row items-center gap-6 mt-12 pt-8 border-t border-slate-100 dark:border-slate-800">
                                        {step > 1 && (
                                            <Button
                                                variant="ghost"
                                                onClick={handleBack}
                                                disabled={isSubmitting}
                                                className="w-full md:w-auto h-14 px-8 rounded-2xl font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                            >
                                                <ArrowLeft className="h-5 w-5 mr-2" /> Volver
                                            </Button>
                                        )}
                                        <Button
                                            className="w-full h-18 rounded-[1.25rem] text-xl font-black bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-xl shadow-orange-600/20 hover:shadow-orange-600/40 hover:scale-[1.02] transition-all flex-1"
                                            onClick={step === 3 ? handleSubmit : handleNext}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center gap-3">
                                                    <Loader2 className="h-7 w-7 animate-spin" /> Procesando...
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    {step === 3 ? "Completar Inscripción" : "Siguiente Paso"}
                                                    <ArrowRight className="h-7 w-7 ml-2" />
                                                </div>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Footer Info */}
                        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6 px-4">
                            <div className="flex items-center gap-3">
                                <Building2 className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-500">{gym.address || gym.city}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-success" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Secure Payment & Data Privacy</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
